import React, { useState, useRef } from "react";
import { Modal, FlyoutFullScreen } from "@accessdev/arc-ui-react";
import { useLocation } from "react-router";
import { connect, useSelector } from "react-redux";
import { selectors } from "@ReduxStore/reducers";
import { Creators } from "@ReduxStore/actionTypes";
import { Member, AuthModalTypes } from "@Src/constants";
import AuthHeader from "./AuthHeader";
import SignIn from "./SignIn";
import SignUp from "./SignUp";
import Media from "react-media";
import { mediaBreakpoints } from "@accessdev/arc-ui-react/utils/js";
import "./Auth.scss";
import { setCookie } from "@Src/utils/cookie.utils";
import AuthInfo from "./AuthInfo";
import { Trans } from "@lingui/react/macro";
const { setAuthOpen, setAuthScreen, resetAuth } = Creators;
const { getAuthenticationIsOpen, getAuthenticationScreen } = selectors;
const { COMPLETE_PROFILE, SIGN_IN, SIGN_UP, PROVIDE_PASSWORD } = AuthModalTypes;

function Auth({ isAuthOpen, setAuthOpen, screen, setAuthScreen, resetAuth }) {
  const { state: locationState } = useLocation();
  const [isCompleteProfile, setIsCompleteProfile] = useState(false);
  const extensionBlurbScreen = "ExtensionBlurb";
  const isAuthOpenRef = useRef(isAuthOpen);
  const helpNumber = useSelector(selectors.getHelpPhoneNumber);
  const isAdminCompleteProfile = locationState && locationState.isAdminCompleteProfile;

  const isAuthScreen = screen === SIGN_IN || screen === PROVIDE_PASSWORD;

  const handleResetAuth = React.useCallback(() => {
    // having this inside a separate usCallback method fixes a react level useEffect return error
    resetAuth();
  }, [resetAuth]);

  React.useEffect(() => {
    setIsCompleteProfile(screen === COMPLETE_PROFILE);
  }, [screen]);

  React.useEffect(() => {
    if (!isAuthOpen && isAuthOpenRef.current) {
      // fires when auth modal closes
      return handleResetAuth();
    }
    isAuthOpenRef.current = isAuthOpen;
  }, [handleResetAuth, isAuthOpen]);

  const AuthForm = ({ isVariant }) => {
    function handleAuthSuccess() {
      setCookie(Member.CVT_SESSION, "true", 1);
      setAuthOpen(false);
    }

    if (isAuthScreen) {
      return <SignIn handleAuthSuccess={handleAuthSuccess} />;
    } else {
      return (
        <SignUp
          handleAuthSuccess={handleAuthSuccess}
          screen={screen}
          isCompleteProfile={isCompleteProfile}
        />
      );
    }
  };

  const AuthMain = ({ isPhone }) =>
    !isPhone ? (
      <>
        <AuthHeader
          screen={screen}
          setAuthScreen={setAuthScreen}
          isCompleteProfile={isCompleteProfile}
          isAdminCompleteProfile={isAdminCompleteProfile}
        />
        <main className="Auth-modalMain" role="tabpanel">
          <div className="Auth-modalForm">
            <AuthForm />
          </div>
          {isAuthScreen && (
            <span className="Auth-getHelp t-body-small">
              <Trans>
                Having trouble? Call <strong>{helpNumber}</strong> for help setting up your account.
              </Trans>
            </span>
          )}
        </main>
      </>
    ) : (
      <>
        <AuthHeader
          screen={screen}
          isCompleteProfile={isCompleteProfile}
          isAdminCompleteProfile={isAdminCompleteProfile}
          setAuthOpen={setAuthOpen}
          mobile={true}
        />
        <main className="Auth-flyoutMain">
          <div className="Auth-flyoutForm">
            <AuthForm />
          </div>
          <FlyoutBottomTxt />
        </main>
      </>
    );

  const FlyoutBottomTxt = () => {
    if (screen === SIGN_IN) {
      return (
        <span className="Auth-noAccount t-body-med">
          <Trans>Don't have an account?</Trans>{" "}
          <button
            className="Auth-mobileSignUpBtn t-label-large"
            onClick={() => setAuthScreen(SIGN_UP)}
          >
            <Trans>Sign Up</Trans>
          </button>
        </span>
      );
    }
    return null;
  };

  const authModalImageClassName =
    screen === extensionBlurbScreen ? "Auth-extensionImage" : "AuthInfo-modalImage";

  return (
    <React.Fragment>
      {isAuthOpen && (
        <Media query={mediaBreakpoints.forPhoneOnly}>
          {(isPhone) =>
            !isPhone ? (
              <Modal
                {...{
                  ...(!isCompleteProfile && {
                    onClose: () => setAuthOpen(false),
                  }),
                  className: isCompleteProfile ? "Auth-modal" : "",
                  defaultPadding: false,
                }}
              >
                <div className="Auth-modalContainer">
                  <AuthInfo screen={screen} isPhone={isPhone} classes={authModalImageClassName} />
                  <div className="Auth-modalContent">
                    <AuthMain isPhone={false} />
                  </div>
                </div>
              </Modal>
            ) : (
              <FlyoutFullScreen variant="Auth-flyout" defaultPadding={false} isOpen={isAuthOpen}>
                <div className="Auth-flyoutContainer">
                  <div className="Auth-flyoutContent">
                    <AuthMain isPhone={true} />
                    <AuthInfo screen={screen} isPhone={isPhone} />
                  </div>
                </div>
              </FlyoutFullScreen>
            )
          }
        </Media>
      )}
    </React.Fragment>
  );
}

const mapStateToProps = (state) => ({
  isAuthOpen: getAuthenticationIsOpen(state),
  screen: getAuthenticationScreen(state),
});

export default connect(mapStateToProps, {
  setAuthOpen,
  setAuthScreen,
  resetAuth,
})(Auth);
